import styled from "styled-components";
import { motion } from "framer-motion";

// Full-bleed biome band. Each section owns its color field (sampled from the
// portfolio-objective screenshots) and sits inside a literal thick page frame —
// dell-1996 treats the viewport as a printed picture frame (DESIGN.md:354).
//
// tone = hero | about | experience | projects | connect | footer

const light = (tone) => tone === "experience" || tone === "connect";

const at = (theme, tone) => theme.biome[tone] || theme.biome.about;

export const SectionWrap = styled.section`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  background: ${({ tone, theme }) => at(theme, tone).bg};
  color: ${({ tone, theme }) => at(theme, tone).ink};

  /* The page frame. Collapses on small screens but never disappears. */
  border-top: ${({ theme }) => theme.frameWidth} solid
    ${({ theme }) => theme.frameInk};
  border-bottom: ${({ theme }) => theme.frameWidth} solid
    ${({ theme }) => theme.frameInk};

  padding: 72px 24px;

  @media (max-width: 768px) {
    border-top-width: ${({ theme }) => theme.frameWidthSm};
    border-bottom-width: ${({ theme }) => theme.frameWidthSm};
    padding: 48px 16px;
  }
`;

// Content column. Wide by default — dell-1996 is a dense catalog, not a narrow
// editorial measure.
export const SectionInner = styled.div`
  width: 100%;
  max-width: ${({ narrow, theme }) =>
    narrow ? theme.maxWidthNarrow : theme.maxWidth};
  display: flex;
  flex-direction: column;
`;

// Header cluster. Left-aligned, matching the objective — not centered.
export const SectionHead = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 8px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 28px;
  }
`;

// Uppercase Helvetica category label in the biome accent — the objective's
// eyebrow, matching dell-1996's `ui-label` (12px / 700 / caps).
export const Eyebrow = styled(motion.div)`
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 2.4px;
  text-transform: uppercase;
  color: ${({ tone, theme }) => at(theme, tone).accent};
`;

// Arial Black, weight 900, line-height 1.0, zero tracking. dell-1996's display
// register depends on extreme weight against flat color (DESIGN.md:413-418).
export const SectionTitle = styled(motion.h2)`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.sectionTitle};
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0;
  color: ${({ tone, theme }) => at(theme, tone).ink};
  margin: 0;
`;

// Clipping wrapper for the title's wipe-up reveal. The extra bottom padding +
// negative margin keeps descenders from being shaved by the mask.
export const MaskLine = styled.span`
  display: block;
  overflow: hidden;
  padding-bottom: 0.12em;
  margin-bottom: -0.12em;
`;

export const SectionDesc = styled(motion.p)`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4;
  color: ${({ tone, theme }) => at(theme, tone).muted};
  max-width: 560px;
  margin: 4px 0 0;
`;

// Hard 1px rule with an inline caps label. Carries the "SIDE QUESTS" and
// "ACHIEVEMENTS UNLOCKED" band headers and the roman-numeral project dividers.
export const RuleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin: ${({ $my }) => $my || "48px 0 28px"};

  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 2.4px;
  text-transform: uppercase;
  color: ${({ tone, theme }) => at(theme, tone).accent};

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${({ tone }) =>
      light(tone) ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.18)"};
  }

  /* Leading rule only, when the label should sit at the left edge. */
  ${({ $lead }) =>
    $lead &&
    `
    &::before { display: none; }
  `}
`;
